import { useCallback } from 'react'

import useWebinarPrivateChannel from '../../hooks/useWebinarPrivateChannel'
import { mapDTOToSpeaker } from '../../hooks/useSpeakersQuery'
import useAudiencePublicChannel from '../../hooks/useAudiencePublicChannel'
import useWebinarPublicChannel from '../../hooks/useWebinarPublicChannel'

import type {
  SpeakerAddedEvent,
  SpeakerEventDTO,
  SpeakerUpdatedEvent,
  SpeakerRemovedEvent,
  JoinToSpeakApprovedEvent,
  JoinToSpeakDeniedEvent,
  JoinToSpeakEventDTO,
  JoinToSpeakExpiredEvent,
} from './types'
import { SpeakerEventType, JoinToSpeakEventType } from './event-types'

function useOnSpeakerAdded(
  callback: (message: SpeakerAddedEvent) => void
): void {
  useWebinarPrivateChannel<SpeakerEventDTO>(
    '/speaker_events',
    useCallback<(messageDTO: SpeakerEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === SpeakerEventType.Added) {
          const message: SpeakerAddedEvent = {
            event: SpeakerEventType.Added,
            speaker: mapDTOToSpeaker(messageDTO.payload),
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

function useOnSpeakerUpdated(
  callback: (message: SpeakerUpdatedEvent) => void
): void {
  useWebinarPrivateChannel<SpeakerEventDTO>(
    '/speaker_events',
    useCallback<(messageDTO: SpeakerEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === SpeakerEventType.Updated) {
          const message: SpeakerUpdatedEvent = {
            event: SpeakerEventType.Updated,
            speaker: mapDTOToSpeaker(messageDTO.payload),
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

function useOnSpeakerRemoved(
  callback: (message: SpeakerRemovedEvent) => void
): void {
  useWebinarPrivateChannel<SpeakerEventDTO>(
    '/speaker_events',
    useCallback<(messageDTO: SpeakerEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === SpeakerEventType.Removed) {
          const message: SpeakerRemovedEvent = {
            event: SpeakerEventType.Added,
            speakerId: messageDTO.payload.live_speaker_id,
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

function useOnApproved(
  callback: (message: JoinToSpeakApprovedEvent) => Promise<void>
): void {
  useAudiencePublicChannel(
    '/join_to_speak_events',
    useCallback<(messageDTO: JoinToSpeakEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === JoinToSpeakEventType.Approved) {
          const message: JoinToSpeakApprovedEvent = {
            event: JoinToSpeakEventType.Approved,
            data: {
              joinToSpeakID: messageDTO.payload.live_speaker_id,
              roomLink: messageDTO.payload.room_link,
            },
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

function useOnDenied(
  callback: (message: JoinToSpeakDeniedEvent) => void
): void {
  useAudiencePublicChannel(
    '/join_to_speak_events',
    useCallback<(messageDTO: JoinToSpeakEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === JoinToSpeakEventType.Denied) {
          const message: JoinToSpeakDeniedEvent = {
            event: JoinToSpeakEventType.Denied,
            data: { joinToSpeakID: messageDTO.payload.live_speaker_id },
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

function useOnExpired(
  callback: (message: JoinToSpeakExpiredEvent) => void
): void {
  useWebinarPublicChannel(
    '/join_to_speak_events',
    useCallback<(messageDTO: JoinToSpeakEventDTO) => void>(
      (messageDTO) => {
        if (messageDTO.event === JoinToSpeakEventType.Expired) {
          const message: JoinToSpeakExpiredEvent = {
            event: JoinToSpeakEventType.Expired,
            data: {
              joinToSpeakID: messageDTO.payload.live_speaker_id,
              roomLink: messageDTO.payload.live_link,
            },
          }

          callback(message)
        }
      },
      [callback]
    )
  )
}

export {
  useOnSpeakerAdded,
  useOnSpeakerUpdated,
  useOnSpeakerRemoved,
  useOnApproved,
  useOnDenied,
  useOnExpired,
}
